import { toast } from "sonner";

export enum ErrorType {
  VALIDATION = "VALIDATION",
  NETWORK = "NETWORK",
  SERVER = "SERVER",
  UNAUTHORIZED = "UNAUTHORIZED",
  FORBIDDEN = "FORBIDDEN",
  NOT_FOUND = "NOT_FOUND",
  UNKNOWN = "UNKNOWN",
}

export interface ApiError {
  type: ErrorType;
  status?: number;
  message: string;
  details?: string;
  originalError?: unknown;
}

interface ErrorMessages {
  title: string;
  description: string;
}

const ERROR_MESSAGES: Record<ErrorType, ErrorMessages> = {
  [ErrorType.VALIDATION]: {
    title: "Dados inválidos",
    description: "Verifique os dados informados e tente novamente.",
  },
  [ErrorType.NETWORK]: {
    title: "Erro de conexão",
    description: "Verifique sua conexão com a internet e tente novamente.",
  },
  [ErrorType.SERVER]: {
    title: "Erro no servidor",
    description: "Ocorreu um erro interno. Estamos trabalhando para resolver o mais rápido possível.",
  },
  [ErrorType.UNAUTHORIZED]: {
    title: "Não autorizado",
    description: "Sua sessão expirou. Faça login novamente.",
  },
  [ErrorType.FORBIDDEN]: {
    title: "Acesso negado",
    description: "Você não tem permissão para acessar este recurso.",
  },
  [ErrorType.NOT_FOUND]: {
    title: "Não encontrado",
    description: "O recurso solicitado não foi encontrado.",
  },
  [ErrorType.UNKNOWN]: {
    title: "Erro desconhecido",
    description: "Ocorreu um erro inesperado. Tente novamente mais tarde.",
  },
};

export function classifyError(error: unknown): ApiError {
  // Network errors (fetch failed, no internet, etc.)
  if (error instanceof TypeError && error.message.includes("fetch")) {
    return {
      type: ErrorType.NETWORK,
      message: ERROR_MESSAGES[ErrorType.NETWORK].description,
      originalError: error,
    };
  }

  // Check for network-related errors
  if (error instanceof Error) {
    const networkKeywords = ["network", "internet", "offline", "ERR_NETWORK", "ECONNREFUSED", "ETIMEDOUT"];
    if (networkKeywords.some((keyword) => error.message.toLowerCase().includes(keyword.toLowerCase()))) {
      return {
        type: ErrorType.NETWORK,
        message: ERROR_MESSAGES[ErrorType.NETWORK].description,
        originalError: error,
      };
    }
  }

  return {
    type: ErrorType.UNKNOWN,
    message: error instanceof Error ? error.message : "Erro desconhecido",
    originalError: error,
  };
}

export function classifyHttpError(status: number, responseBody?: unknown): ApiError {
  let type: ErrorType;
  let details: string | undefined;

  // Extract details from response body if available
  if (responseBody && typeof responseBody === "object") {
    const body = responseBody as Record<string, unknown>;
    details = (body.detail as string) || (body.message as string) || (body.title as string);
  }

  if (status >= 400 && status < 500) {
    switch (status) {
      case 400:
        type = ErrorType.VALIDATION;
        break;
      case 401:
        type = ErrorType.UNAUTHORIZED;
        break;
      case 403:
        type = ErrorType.FORBIDDEN;
        break;
      case 404:
        type = ErrorType.NOT_FOUND;
        break;
      case 422:
        type = ErrorType.VALIDATION;
        break;
      default:
        type = ErrorType.VALIDATION;
    }
  } else if (status >= 500) {
    type = ErrorType.SERVER;
  } else {
    type = ErrorType.UNKNOWN;
  }

  const messages = ERROR_MESSAGES[type];

  return {
    type,
    status,
    message: messages.description,
    details,
  };
}

export function showErrorToast(error: ApiError, options?: { skipNotFound?: boolean }): void {
  // Skip toast for NOT_FOUND errors - they will be handled visually with Lottie animation
  if (error.type === ErrorType.NOT_FOUND && options?.skipNotFound !== false) {
    return;
  }

  const messages = ERROR_MESSAGES[error.type];

  const description = error.details 
    ? `${messages.description} ${error.details}` 
    : messages.description;

  switch (error.type) {
    case ErrorType.NETWORK:
      toast.error(messages.title, {
        description,
        duration: 5000,
        icon: "🌐",
      });
      break;

    case ErrorType.SERVER:
      toast.error(messages.title, {
        description,
        duration: 6000,
        icon: "⚠️",
      });
      break;

    case ErrorType.VALIDATION:
      toast.warning(messages.title, {
        description,
        duration: 5000,
        icon: "📝",
      });
      break;

    case ErrorType.UNAUTHORIZED:
      toast.error(messages.title, {
        description,
        duration: 4000,
        icon: "🔒",
      });
      break;

    case ErrorType.FORBIDDEN:
      toast.error(messages.title, {
        description,
        duration: 4000,
        icon: "🚫",
      });
      break;

    case ErrorType.NOT_FOUND:
      toast.warning(messages.title, {
        description,
        duration: 4000,
        icon: "🔍",
      });
      break;

    default:
      toast.error(messages.title, {
        description,
        duration: 5000,
      });
  }
}

export function handleApiError(status: number, responseBody?: unknown): ApiError {
  const error = classifyHttpError(status, responseBody);
  showErrorToast(error);
  return error;
}

export function handleNetworkError(error: unknown): ApiError {
  const classifiedError = classifyError(error);
  showErrorToast(classifiedError);
  return classifiedError;
}

export default {
  classifyError,
  classifyHttpError,
  showErrorToast,
  handleApiError,
  handleNetworkError,
  ErrorType,
};
